<template>
  <div class="p-4">
    <div v-if="loading">Loading...</div>
    <div v-if="error">{{ error }}</div>

    <div v-if="lots">
      <module-table :lots="lots" />
    </div>

    <div v-if="data">
      <!-- Таблица статистики по качествам -->
      <template v-if="data?.attributes">
        <div v-for="(fixItem, fix) in data.attributes" :key="fix">
          <h1>{{ fix }}</h1>
          <Card style="background-color: #4c515e; margin-bottom: 40px">
            <template #content>
              <div class="grid-elem">
                <div
                  v-for="(elem, attr, index) in fixItem"
                  :key="`${fix}-${attr || 'unknown'}-${index}`"
                >
                  {{ getStatName(attr, fix)?.nameRu }}
                  ({{ getStatName(attr, fix)?.parameterRu }})
                  <DataTable :value="Object.values(elem)">
                    <Column field="quality" header="Quality" />
                    <Column field="avgPrice" header="Avg Price">
                      <template #body="{ data }">
                        {{ formatPrice(data.avgPrice) }}
                      </template>
                    </Column>
                    <Column field="salesCount" header="Sales Count" />
                    <Column field="liquidityPerDay" header="Liquidity/Day">
                      <template #body="{ data }">
                        {{ data.liquidityPerDay?.toFixed(2) }}
                      </template>
                    </Column>
                    <Column field="popularity" header="Popularity">
                      <template #body="{ data }">
                        <div class="popularity-bar">
                          <div
                            class="popularity-fill"
                            :style="{
                              width: `${(data.popularity || 0) * 100}%`
                            }"
                          ></div>
                          <span>{{ ((data.popularity || 0) * 100).toFixed(1) }}%</span>
                        </div>
                      </template>
                    </Column>
                  </DataTable>
                </div>
              </div>
            </template>
          </Card>
        </div>
      </template>

      <!-- Популярные комбинации -->
      <template v-if="data?.popularPairs">
        <h1>Популярные комбинации</h1>
        <Card style="background-color: #4c515e; margin-bottom: 40px">
          <template #content>
            <div v-for="(pairs, category) in data.popularPairs" :key="category" class="mb-6">
              <h3>{{ category }}</h3>
              <DataTable
                :value="
                  Array.isArray(pairs) ? pairs.slice(0, 10) : Object.values(pairs).slice(0, 10)
                "
              >
                <Column header="Комбинация">
                  <template #body="{ data }">
                    <div class="combo-display">
                      <span
                        v-for="(attr, idx) in data.attributes"
                        :key="attrKey(attr) || `attr-${idx}`"
                        class="attr-badge"
                        :class="`quality-${attr.quality}`"
                      >
                        {{ getStatName(attr.definitionId, attr.slot)?.nameRu }}
                      </span>
                    </div>
                  </template>
                </Column>
                <Column field="avgPrice" header="Avg Price">
                  <template #body="{ data }">
                    {{ formatPrice(data.avgPrice) }}
                  </template>
                </Column>
                <Column field="salesCount" header="Sales" />
                <Column field="liquidityPerDay" header="Liquidity/Day">
                  <template #body="{ data }">
                    {{ data.liquidityPerDay?.toFixed(2) }}
                  </template>
                </Column>
                <Column field="synergy" header="Синергия">
                  <template #body="{ data }">
                    <div class="synergy-indicator">
                      <div class="synergy-bar">
                        <div
                          class="synergy-fill"
                          :style="{ width: `${(data.synergy || 0) * 100}%` }"
                        ></div>
                      </div>
                      <span>{{ ((data.synergy || 0) * 100).toFixed(0) }}%</span>
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </template>
        </Card>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Card from 'primevue/card'
import ModuleTable from '@/components/module/ModuleTable.vue'
import { formatPrice } from '@/common/function'
import { MODULE_STATS, QUALITY_LIST } from '@/components/module/data'
import { api } from '@/api'

type Attr = {
  slot: string
  definitionId: string
  quality: number
}

type CombinationStat = {
  attributes: Attr[]
  avgPrice: number
  salesCount: number
  liquidityPerDay: number
  synergy?: number
  popularity?: number
  priceVariance?: number
}

type AttributeStat = {
  slot: string
  definitionId: string
  quality: number
  avgPrice: number
  salesCount: number
  liquidityPerDay: number
  priceVariance?: number
  medianPrice?: number
  popularity?: number
}

interface MarketAnalytics {
  attributes: Record<string, Record<string, Record<number, AttributeStat>>>
  combinations: CombinationStat[]
  popularPairs: Record<string, CombinationStat[]>
  synergyMatrix: Record<string, number>
}

const data = ref<MarketAnalytics | null>(null)
const lots = ref<any[]>([])
const loading = ref(false)
const error = ref('')

/* ------------------------------------------------ */
function attrKey(a: Attr) {
  return `${a.slot}*${a.definitionId}*${a.quality}`
}

function comboKey(attrs: Attr[]) {
  return attrs.map(attrKey).sort().join('|')
}

/* ------------------------------------------------ */
const comboIndex = computed(() => {
  const map = new Map<string, CombinationStat>()
  if (!data.value?.combinations) return map

  for (const c of data.value.combinations) {
    map.set(comboKey(c.attributes), c)
  }

  return map
})

/* ------------------------------------------------ */
function mapSlot(value: number) {
  if (value === 0) return 'prefix'
  if (value === 1) return 'suffix'
  if (value === 2) return 'affix'
  return 'prefix'
}

/* ------------------------------------------------ */
function normalizeAttributes(attrs: any[]): Record<string, Attr | null> {
  const result: {
    prefix: Attr | null
    suffix: Attr | null
    affix: Attr | null
  } = {
    prefix: null,
    suffix: null,
    affix: null
  }

  if (!attrs) return result

  for (const a of attrs) {
    const slot = mapSlot(a.type)
    if (!slot) continue
    result[slot] = {
      slot,
      definitionId: a.definitionId,
      quality: a.quality ?? 0
    }
  }

  return result
}

/* ------------------------------------------------ */
function findCombos(prefix: Attr | null, suffix: Attr | null, affix: Attr | null) {
  const index = comboIndex.value
  const result = { triple: null, AB: null, AC: null, BC: null }

  if (prefix && suffix) result.AB = index.get(comboKey([prefix, suffix])) ?? null
  if (prefix && affix) result.AC = index.get(comboKey([prefix, affix])) ?? null
  if (suffix && affix) result.BC = index.get(comboKey([suffix, affix])) ?? null
  if (prefix && suffix && affix)
    result.triple = index.get(comboKey([prefix, suffix, affix])) ?? null

  return result
}

/* ------------------------------------------------ */
function getSingleStat(attr: Attr | null) {
  if (!attr || !data.value?.attributes) return null
  return data.value.attributes[attr.slot]?.[attr.definitionId]?.[attr.quality] ?? null
}

/* ------------------------------------------------ */
/* Персентиль для фильтрации аномальных цен */
function percentile(arr: number[], q: number) {
  if (!arr.length) return 0
  const sorted = [...arr].sort((a, b) => a - b)
  const pos = (sorted.length - 1) * q
  const base = Math.floor(pos)
  const rest = pos - base
  if (sorted[base + 1] !== undefined) return sorted[base] + rest * (sorted[base + 1] - sorted[base])
  return sorted[base]
}

/* ------------------------------------------------ */
function filterStatsByPercentile(stats: AttributeStat[]) {
  if (!stats.length) return stats

  // Разделяем на высоколиквидные и низколиквидные
  const highLiquidity = stats.filter(s => s.liquidityPerDay >= 1)
  const lowLiquidity = stats.filter(s => s.liquidityPerDay < 1)

  let filtered = [...stats]

  // Сначала отсекаем явные RMT выбросы (продажи по 80M+)
  filtered = filtered.filter(s => s.avgPrice < 50000000) // отсекаем все что выше 50M

  // Для высоколиквидных применяем строгую фильтрацию
  if (highLiquidity.length >= 3) {
    const prices = highLiquidity.map(s => s.avgPrice).filter(p => p > 0 && p < 50000000)
    if (prices.length) {
      const low = percentile(prices, 0.1) // Уменьшили нижний порог
      const high = percentile(prices, 0.9) // Уменьшили верхний порог
      filtered = highLiquidity.filter(
        s => s.avgPrice >= low && s.avgPrice <= high && s.avgPrice < 50000000
      )
    }
  }

  // Для низколиквидных используем более мягкую фильтрацию
  if (lowLiquidity.length) {
    const prices = lowLiquidity.map(s => s.avgPrice).filter(p => p > 0 && p < 50000000)
    if (prices.length >= 2) {
      const median = percentile(prices, 0.5)
      const maxDeviation = Math.min(median * 3, 20000000) // ограничиваем максимум 20M
      const lowLiqFiltered = lowLiquidity.filter(
        s => s.avgPrice <= maxDeviation && s.avgPrice < 50000000
      )
      filtered = [...filtered, ...lowLiqFiltered]
    }
  }

  // Дополнительная проверка на аномальные цены для редких модулей
  if (filtered.length >= 5) {
    const allPrices = filtered.map(s => s.avgPrice).filter(p => p > 0)
    const globalMedian = percentile(allPrices, 0.5)

    // Отсекаем цены которые в 10+ раз выше медианы
    filtered = filtered.filter(s => s.avgPrice <= globalMedian * 10)
  }

  // Если после фильтрации осталось мало данных, возвращаем отфильтрованные от RMT
  if (filtered.length < 2) {
    return stats.filter(s => s.avgPrice < 50000000)
  }

  return filtered
}

function estimateMarketPriceWithPairs(pairs: any, singles: any) {
  const affix = singles.C ?? { avgPrice: 0, liquidityPerDay: 0 }
  const prefix = singles.A ?? { avgPrice: 0, liquidityPerDay: 0 }
  const suffix = singles.B ?? { avgPrice: 0, liquidityPerDay: 0 }

  // Берем пары и тройку
  const triple = pairs.triple ?? null
  const AC = pairs.AC ?? null // affix + prefix
  const BC = pairs.BC ?? null // affix + suffix

  // const AB = pairs.AB ?? null // prefix + suffix

  return {
    price: triple?.avgPrice,
    liquidityPerDay: triple?.liquidityPerDay,
    salesCount: triple?.salesCount,
    affixPrice: affix?.avgPrice,
    affixLiquidityPerDay: affix?.liquidityPerDay,
    affixSalesCount: affix?.salesCount,
    AC,
    BC
  }

  // Сначала собираем все доступные цены для ограничений
  // const allPrices = [
  //   triple?.avgPrice,
  //   AC?.avgPrice,
  //   BC?.avgPrice,
  //   AB?.avgPrice,
  //   affix.avgPrice,
  //   prefix.avgPrice,
  //   suffix.avgPrice
  // ].filter(p => p > 0)

  // Тройка имеет наибольший приоритет, если есть
  // elements.push({
  //   price: triple.avgPrice,
  //   label: 'трёхкомбинация'
  // })

  // Пары с учетом синергии
  // if (AC?.liquidityPerDay > 0 && AC.avgPrice <= maxAllowedPrice) {
  //   elements.push({
  //     price: Math.min(AC.avgPrice, maxAllowedPrice),
  //     weight:
  //       AC.liquidityPerDay * (weightAffix + weightPrefix) * (synergyScore > 0.6 ? synergyBonus : 1),
  //     label: 'аффикс+префикс'
  //   })
  // }
  // if (BC?.liquidityPerDay > 0 && BC.avgPrice <= maxAllowedPrice) {
  //   elements.push({
  //     price: Math.min(BC.avgPrice, maxAllowedPrice),
  //     weight:
  //       BC.liquidityPerDay * (weightAffix + weightSuffix) * (synergyScore > 0.6 ? synergyBonus : 1),
  //     label: 'аффикс+суффикс',
  //     synergy: synergyScore
  //   })
  // }
  // if (AB?.liquidityPerDay > 0 && AB.avgPrice <= maxAllowedPrice) {
  //   elements.push({
  //     price: Math.min(AB.avgPrice, maxAllowedPrice),
  //     weight:
  //       AB.liquidityPerDay *
  //       (weightPrefix + weightSuffix) *
  //       (synergyScore > 0.6 ? synergyBonus : 1),
  //     label: 'префикс+суффикс',
  //     synergy: synergyScore
  //   })
  // }
  //
  // // Одиночные статы с ограничением цены
  // if (affix.liquidityPerDay > 0 && affix.avgPrice <= maxAllowedPrice)
  //   elements.push({
  //     price: Math.min(affix.avgPrice, maxAllowedPrice),
  //     weight: affix.liquidityPerDay * weightAffix,
  //     label: 'аффикс'
  //   })
  // if (prefix.liquidityPerDay > 0 && prefix.avgPrice <= maxAllowedPrice)
  //   elements.push({
  //     price: Math.min(prefix.avgPrice, maxAllowedPrice),
  //     weight: prefix.liquidityPerDay * weightPrefix,
  //     label: 'префикс'
  //   })
  // if (suffix.liquidityPerDay > 0 && suffix.avgPrice <= maxAllowedPrice)
  //   elements.push({
  //     price: Math.min(suffix.avgPrice, maxAllowedPrice),
  //     weight: suffix.liquidityPerDay * weightSuffix,
  //     label: 'суффикс'
  //   })
}

/* ------------------------------------------------ */
function calculateLotPriority(lot: any) {
  const attrs = normalizeAttributes(lot?.additional?.attributes)
  const pairs = findCombos(attrs.prefix, attrs.suffix, attrs.affix)
  const singles = {
    A: getSingleStat(attrs.prefix),
    B: getSingleStat(attrs.suffix),
    C: getSingleStat(attrs.affix)
  }

  if (singles.A) singles.A = filterStatsByPercentile([singles.A])[0] ?? singles.A
  if (singles.B) singles.B = filterStatsByPercentile([singles.B])[0] ?? singles.B
  if (singles.C) singles.C = filterStatsByPercentile([singles.C])[0] ?? singles.C

  const info = estimateMarketPriceWithPairs(pairs, singles)

  return {
    ...lot,
    information: info,
    prefix: {
      ...attrs.prefix,
      name: getStatName(attrs.prefix?.definitionId, attrs.prefix?.slot)?.nameRu
    },
    suffix: {
      ...attrs.suffix,
      name: getStatName(attrs.suffix?.definitionId, attrs.suffix?.slot)?.nameRu
    },
    affix: {
      ...attrs.affix,
      name: getStatName(attrs.affix?.definitionId, attrs.affix?.slot)?.nameRu
    }
  }
}

/* ------------------------------------------------ */
async function fetchModulesStat() {
  loading.value = true
  try {
    const res = await api.get('/module-stat/analytics')
    data.value = res.data
  } catch (e) {
    error.value = 'API error'
  }
  loading.value = false
}

/* ------------------------------------------------ */
async function fetchModulesLots() {
  loading.value = true
  try {
    const res = await api.get('/module-stat/lots')
    const rawLots = res.data ?? []
    lots.value = rawLots.filter(i => i.buyoutPrice).map(calculateLotPriority)
  } finally {
    loading.value = false
  }
}

/* ------------------------------------------------ */
onMounted(async () => {
  await fetchModulesStat()
  await fetchModulesLots()
})

/* ------------------------------------------------ */
const getStatName = (definitionId, slot) => {
  const cleaned = definitionId?.replace(/_(pre|suf|aff)$/g, '').replace(/_(pre|suf|aff)_?/g, '')
  const stat = MODULE_STATS.find(s => s.id === cleaned && s.slots?.includes(slot))
  return stat || cleaned
}

const getQuality = quality => QUALITY_LIST.find(q => q.value === quality)
</script>

<style scoped>
.mb-4 {
  margin-bottom: 1rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.grid-elem {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 16px;
}

/* Популярность и синергия */
.popularity-bar,
.synergy-bar {
  width: 60px;
  height: 8px;
  background-color: #2c2c2c;
  border-radius: 4px;
  overflow: hidden;
  display: inline-block;
  margin-right: 8px;
  vertical-align: middle;
}

.popularity-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #34d399);
  transition: width 0.3s ease;
}

.synergy-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  transition: width 0.3s ease;
}

.popularity-bar span,
.synergy-indicator span {
  font-size: 0.8em;
  color: #9ca3af;
}

.synergy-indicator {
  display: flex;
  align-items: center;
}

/* Комбинации атрибутов */
.combo-display {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.attr-badge {
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 0.75em;
  font-weight: 500;
  white-space: nowrap;
}

/* Цвета по качеству */
.quality-0 {
  background-color: rgba(128, 128, 128, 0.3);
  color: #a0a0a0;
  border: 1px solid #7c7cff;
}
.quality-1 {
  background-color: rgba(144, 238, 144, 0.3);
  color: #90ee90;
  border: 1px solid #90ee90;
}
.quality-2 {
  background-color: rgba(135, 206, 235, 0.3);
  color: #87ceeb;
  border: 1px solid #87ceeb;
}
.quality-3 {
  background-color: rgba(147, 112, 219, 0.3);
  color: #dda0dd;
  border: 1px solid #dda0dd;
}
.quality-4 {
  background-color: rgba(220, 20, 60, 0.3);
  color: #dc143c;
  border: 1px solid #dc143c;
}
.quality-5 {
  background-color: rgba(255, 215, 0, 0.3);
  color: #ffd700;
  border: 1px solid #ffd700;
}
</style>
